import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ProjectTask, Many2one } from '@/types/odoo';
import { useProjectViewStore } from '@/stores/projectView';
import { usePermissionsStore } from '@/stores/permissions';
import { usePlanningStore } from '@/stores/planning';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { createProjectFase, scheduleFase, fetchInstallers, updatePlanningTask } from '@/api/planning';
import type { InstallerRecord } from '@/api/planning';
import { InstallerToggleDropdown } from '@/components/ui/InstallerToggleDropdown';
import type { PlanningStage } from '@/api/tasks';

let cachedInstallerData: { userMap: Map<number, string>; categoryMap: Map<number, string> } | null = null;
let installerFetchPromise: Promise<InstallerRecord[]> | null = null;

function calcPickerPos(el: HTMLElement): { top?: number; bottom?: number; left: number; maxHeight: number } {
  const rect = el.getBoundingClientRect();
  const pad = 8;
  const spaceBelow = window.innerHeight - rect.bottom - pad;
  const spaceAbove = rect.top - pad;
  return spaceBelow >= spaceAbove
    ? { top: rect.bottom + 4, left: rect.left, maxHeight: spaceBelow }
    : { bottom: window.innerHeight - rect.top + 4, left: rect.left, maxHeight: spaceAbove };
}

async function loadInstallerMaps(): Promise<{ userMap: Map<number, string>; categoryMap: Map<number, string> }> {
  if (cachedInstallerData) return cachedInstallerData;
  if (!installerFetchPromise) installerFetchPromise = fetchInstallers();
  const list = await installerFetchPromise;
  const userMap = new Map<number, string>();
  const categoryMap = new Map<number, string>();
  for (const inst of list) {
    userMap.set(inst.id, inst.name);
    categoryMap.set(inst.id, inst.category);
  }
  cachedInstallerData = { userMap, categoryMap };
  return cachedInstallerData;
}

interface PlanningTabProps {
  projectId: number;
  projectTasks: ProjectTask[];
  currentTaskId: number | null;
  installerNames: Record<string, string>;
}

const STAGE_OPTIONS: Array<{ value: PlanningStage; label: string; className: string }> = [
  { value: 'In uitvoering', label: 'In uitvoering', className: 'bg-amber-100 text-amber-800' },
  { value: 'Mag worden gevorderd', label: 'Gevorderd', className: 'bg-green-600 text-white' },
  { value: 'Einde werken Volledig Project', label: 'Einde werken', className: 'bg-blue-600 text-white' },
];

function isFaseTask(task: ProjectTask): boolean {
  return Array.isArray(task.stage_id) && task.stage_id[1] === 'Fase Indeling' && !task.parent_id;
}

function PlanningStagePill({
  projectId,
  task,
  variant,
  canEdit,
}: {
  projectId: number;
  task: ProjectTask;
  variant: PlanningStageVariant;
  canEdit: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const updateTaskPlanningStage = useProjectViewStore((s) => s.updateTaskPlanningStage);
  const refreshMainPlanning = usePlanningStore((s) => s.fetchPlanning);
  const mainPlanningLoaded = usePlanningStore((s) => s.tasks.length > 0);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const activeStage: PlanningStage =
    task.x_studio_planningstage === 'Mag worden gevorderd'
      ? 'Mag worden gevorderd'
      : task.x_studio_planningstage === 'Einde werken Volledig Project'
        ? 'Einde werken Volledig Project'
        : 'In uitvoering';

  if (!canEdit) {
    return (
      <span
        className={`text-[9px] px-1.5 py-0.5 rounded-full shrink-0 ${variant.className}`}
      >
        {variant.label}
      </span>
    );
  }

  const handleSelect = async (stage: PlanningStage, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    if (stage === activeStage) return;
    setBusy(true);
    const ok = await updateTaskPlanningStage(projectId, task.id, stage);
    setBusy(false);
    if (ok && mainPlanningLoaded) {
      refreshMainPlanning();
    }
  };

  return (
    <div ref={containerRef} className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        disabled={busy}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Planningstage wijzigen"
        className={`text-[9px] px-1.5 py-0.5 rounded-full shrink-0 cursor-pointer hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-td-blue/40 disabled:opacity-60 ${variant.className}`}
      >
        {variant.label}
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-30 py-1 min-w-[140px]"
        >
          {STAGE_OPTIONS.map((opt) => {
            const isActive = opt.value === activeStage;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={(e) => handleSelect(opt.value, e)}
                className={`w-full text-left px-2 py-1.5 text-xs flex items-center gap-2 hover:bg-gray-50 ${
                  isActive ? 'font-semibold' : ''
                }`}
              >
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${opt.className}`}>
                  {opt.label}
                </span>
                {isActive && (
                  <svg className="w-3.5 h-3.5 text-td-blue ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function formatDeadline(dateStr: string | false): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('nl-BE', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateRange(dateStr: string, options: Intl.DateTimeFormatOptions): string {
  return new Date(dateStr).toLocaleDateString('nl-BE', options);
}

function getFaseDateRange(children: ProjectTask[]): string | null {
  const dates = children
    .map((t) => t.date_deadline)
    .filter((d): d is string => typeof d === 'string' && d.length > 0);

  if (dates.length === 0) return null;

  const sorted = [...dates].sort();
  const min = sorted[0];
  const max = sorted[sorted.length - 1];

  if (min === max) {
    return formatDateRange(min, { day: 'numeric', month: 'short', year: 'numeric' });
  }

  const minYear = new Date(min).getFullYear();
  const maxYear = new Date(max).getFullYear();

  const minFormatted = formatDateRange(min, {
    day: 'numeric',
    month: 'short',
    ...(minYear !== maxYear ? { year: 'numeric' } : {}),
  });
  const maxFormatted = formatDateRange(max, { day: 'numeric', month: 'short', year: 'numeric' });

  return `${minFormatted} — ${maxFormatted}`;
}

function stageName(stage: Many2one): string {
  return stage ? stage[1] : '-';
}

type PlanningStageVariant = {
  label: string;
  className: string;
};

function getPlanningStageVariant(stage: string | false | undefined | null): PlanningStageVariant | null {
  if (stage === 'Einde werken Volledig Project') {
    return {
      label: 'Einde werken',
      className: 'bg-blue-600 text-white',
    };
  }
  if (stage === 'Mag worden gevorderd') {
    return {
      label: 'Gevorderd',
      className: 'bg-green-600 text-white',
    };
  }
  if (stage === 'Gemengd') {
    return {
      label: 'Gemengd',
      className: 'bg-gray-100 text-td-muted border border-gray-300',
    };
  }
  if (stage === 'In uitvoering' || !stage) {
    return {
      label: 'In uitvoering',
      className: 'bg-amber-100 text-amber-800',
    };
  }
  return null;
}

function buildTree(tasks: ProjectTask[]) {
  const taskMap = new Map<number, ProjectTask>();
  for (const t of tasks) taskMap.set(t.id, t);

  const roots: ProjectTask[] = [];
  const childrenOf = new Map<number, ProjectTask[]>();

  for (const t of tasks) {
    const parentId = t.parent_id ? t.parent_id[0] : null;
    if (!parentId || !taskMap.has(parentId)) {
      roots.push(t);
    } else {
      const siblings = childrenOf.get(parentId) || [];
      siblings.push(t);
      childrenOf.set(parentId, siblings);
    }
  }

  return { roots, childrenOf };
}

function toDateInputValue(dateStr: string | false): string {
  if (!dateStr) return '';
  return dateStr.slice(0, 10);
}

function InlineDateEdit({ taskId, projectId, currentDate }: { taskId: number; projectId: number; currentDate: string | false }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(toDateInputValue(currentDate));
  const [saving, setSaving] = useState(false);

  const handleSave = async (newDate: string) => {
    if (!newDate || newDate === toDateInputValue(currentDate)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await updatePlanningTask(taskId, { date_deadline: newDate });
      setEditing(false);
      await useProjectViewStore.getState().refreshProject(projectId);
    } catch {
      setValue(toDateInputValue(currentDate));
      setEditing(false);
    }
    setSaving(false);
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setValue(toDateInputValue(currentDate));
          setEditing(true);
        }}
        className="text-xs text-td-muted flex items-center gap-1 hover:text-td-blue transition-colors"
        title="Datum wijzigen"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        {currentDate ? formatDeadline(currentDate) : 'Geen datum'}
      </button>
    );
  }

  return (
    <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <input
        type="date"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => handleSave(value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave(value);
          if (e.key === 'Escape') { setValue(toDateInputValue(currentDate)); setEditing(false); }
        }}
        autoFocus
        disabled={saving}
        className="border border-td-blue rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-2 focus:ring-td-blue/40 disabled:opacity-50"
      />
    </span>
  );
}

function InlineInstallerEdit({ taskId, projectId, currentIds, installerNames }: {
  taskId: number;
  projectId: number;
  currentIds: number[];
  installerNames: Record<string, string>;
}) {
  const [showPicker, setShowPicker] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>(currentIds);
  const [userMap, setUserMap] = useState<Map<number, string>>(new Map());
  const [categoryMap, setCategoryMap] = useState<Map<number, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [fixedPos, setFixedPos] = useState<{ top?: number; bottom?: number; left: number; maxHeight: number } | undefined>();
  const pickerRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setSelectedIds(currentIds);
  }, [currentIds]);

  useEffect(() => {
    if (!showPicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPicker, selectedIds]);

  const handleOpen = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (btnRef.current) setFixedPos(calcPickerPos(btnRef.current));
    if (userMap.size === 0 && !loading) {
      setLoading(true);
      try {
        const data = await loadInstallerMaps();
        setUserMap(data.userMap);
        setCategoryMap(data.categoryMap);
      } catch {
        /* ignore */
      }
      setLoading(false);
    }
    setShowPicker(true);
  };

  const handleToggle = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleClose = () => {
    setShowPicker(false);
    const changed = selectedIds.length !== currentIds.length || selectedIds.some((id) => !currentIds.includes(id));
    if (changed) {
      updatePlanningTask(taskId, { x_studio_installateur: selectedIds })
        .then(() => useProjectViewStore.getState().refreshProject(projectId))
        .catch(() => setSelectedIds(currentIds));
    }
  };

  const instNames = selectedIds.map((id) => installerNames[id] || userMap.get(id) || `#${id}`).filter(Boolean);

  return (
    <div className="flex items-center gap-1 mt-1" onClick={(e) => e.stopPropagation()}>
      <p className="text-xs text-td-muted truncate">
        {instNames.length > 0 ? instNames.join(', ') : <span className="italic">Niet toegewezen</span>}
      </p>
      <button
        ref={btnRef}
        type="button"
        onClick={handleOpen}
        disabled={loading}
        className="p-0.5 rounded text-td-muted hover:text-td-blue transition-colors"
        title="Installateurs bewerken"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      </button>
      {showPicker && fixedPos && createPortal(
        <div ref={pickerRef} style={{ position: 'fixed', zIndex: 9999 }}>
          <InstallerToggleDropdown
            userMap={userMap}
            categoryMap={categoryMap}
            selectedIds={selectedIds}
            onToggle={handleToggle}
            onClose={handleClose}
            fixedPos={fixedPos}
          />
        </div>,
        document.body,
      )}
    </div>
  );
}

function TaskRow({
  projectId,
  task,
  currentTaskId,
  installerNames,
  isChild,
  onClick,
  faseDateRange,
  canEditDate,
}: {
  projectId: number;
  task: ProjectTask;
  currentTaskId: number | null;
  installerNames: Record<string, string>;
  isChild?: boolean;
  onClick?: () => void;
  faseDateRange?: string | null;
  canEditDate?: boolean;
}) {
  const setActiveTask = useProjectViewStore((s) => s.setActiveTask);
  const canEditPlanning = usePermissionsStore((s) => s.canFeature('planning_edit'));
  const isCurrent = task.id === currentTaskId;
  const instNames = (task.x_studio_installateur || [])
    .map((id) => installerNames[id])
    .filter(Boolean);

  return (
    <div
      onClick={() => {
        if (onClick) {
          onClick();
        } else if (!isCurrent) {
          setActiveTask(task.id);
        }
      }}
      role="button"
      tabIndex={0}
      className={`w-full text-left rounded-xl border p-3 transition-all ${
        isChild ? 'ml-10' : ''
      } ${
        isCurrent
          ? 'border-td-blue bg-td-blue/5 ring-2 ring-td-blue/20 cursor-default'
          : 'border-gray-200 bg-white hover:border-td-blue/40 hover:shadow-sm cursor-pointer'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className={`text-sm font-bold truncate ${isCurrent ? 'text-td-blue' : 'text-td-navy'}`}>
              {task.name}
            </p>
            {isCurrent && (
              <span className="shrink-0 text-[10px] font-bold text-td-blue bg-td-blue/10 px-1.5 py-0.5 rounded">
                Huidig
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
            <StatusBadge stage={stageName(task.stage_id)} />

            {(() => {
              const variant = getPlanningStageVariant(task.x_studio_planningstage);
              if (!variant) return null;
              const isEditableStage =
                !task.x_studio_planningstage ||
                task.x_studio_planningstage === 'In uitvoering' ||
                task.x_studio_planningstage === 'Mag worden gevorderd' ||
                task.x_studio_planningstage === 'Einde werken Volledig Project';
              if (canEditPlanning && isEditableStage) {
                return (
                  <PlanningStagePill
                    projectId={projectId}
                    task={task}
                    variant={variant}
                    canEdit
                  />
                );
              }
              return (
                <span
                  className={`text-[9px] px-1.5 py-0.5 rounded-full shrink-0 ${variant.className}`}
                >
                  {variant.label}
                </span>
              );
            })()}

            {faseDateRange && (
              <span className="text-xs text-td-muted flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {faseDateRange}
              </span>
            )}

            {!faseDateRange && canEditDate && (
              <InlineDateEdit taskId={task.id} projectId={projectId} currentDate={task.date_deadline} />
            )}

            {!faseDateRange && !canEditDate && task.date_deadline && (
              <span className="text-xs text-td-muted flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {formatDeadline(task.date_deadline)}
              </span>
            )}

            {task.x_studio_verdieping && (
              <span className="text-xs text-td-muted">{task.x_studio_verdieping}</span>
            )}
          </div>

          {canEditDate && canEditPlanning ? (
            <InlineInstallerEdit
              taskId={task.id}
              projectId={projectId}
              currentIds={task.x_studio_installateur || []}
              installerNames={installerNames}
            />
          ) : instNames.length > 0 ? (
            <p className="text-xs text-td-muted mt-1 truncate">{instNames.join(', ')}</p>
          ) : null}
        </div>

        {!isCurrent && (
          <svg className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        )}
      </div>
    </div>
  );
}

function ScheduleTaskForm({ projectId, faseId }: { projectId: number; faseId: number }) {
  const [expanded, setExpanded] = useState(false);
  const [date, setDate] = useState('');
  const [selectedInstallerIds, setSelectedInstallerIds] = useState<number[]>([]);
  const [userMap, setUserMap] = useState<Map<number, string>>(new Map());
  const [categoryMap, setCategoryMap] = useState<Map<number, string>>(new Map());
  const [loadingInstallers, setLoadingInstallers] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fixedPos, setFixedPos] = useState<{ top?: number; bottom?: number; left: number; maxHeight: number } | undefined>();
  const pickerRef = useRef<HTMLDivElement>(null);
  const installerBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!showPicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPicker]);

  const handleExpand = async () => {
    setExpanded(true);
    setError(null);
    if (userMap.size === 0 && !loadingInstallers) {
      setLoadingInstallers(true);
      try {
        const data = await loadInstallerMaps();
        setUserMap(data.userMap);
        setCategoryMap(data.categoryMap);
      } catch {
        setUserMap(new Map());
        setCategoryMap(new Map());
      }
      setLoadingInstallers(false);
    }
  };

  const handleToggleInstaller = (id: number) => {
    setSelectedInstallerIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!date) return;
    setScheduling(true);
    setError(null);
    try {
      await scheduleFase({
        faseId,
        projectId,
        installerIds: selectedInstallerIds.length > 0 ? selectedInstallerIds : undefined,
        date,
      });
      setDate('');
      setSelectedInstallerIds([]);
      setExpanded(false);
      await useProjectViewStore.getState().refreshProject(projectId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Inplannen mislukt');
    }
    setScheduling(false);
  };

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={handleExpand}
        className="ml-10 mt-1 text-xs text-td-blue hover:text-td-blue/80 flex items-center gap-1"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Inplannen
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="ml-10 mt-2 space-y-2" onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          required
          className="border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-td-blue/40"
        />
        <button
          ref={installerBtnRef}
          type="button"
          onClick={() => {
            if (installerBtnRef.current) setFixedPos(calcPickerPos(installerBtnRef.current));
            setShowPicker((o) => !o);
          }}
          disabled={loadingInstallers}
          className="border border-gray-300 rounded-lg px-2 py-1 text-xs hover:border-td-blue/40 focus:outline-none focus:ring-2 focus:ring-td-blue/40 flex items-center gap-1"
        >
          <svg className="w-3.5 h-3.5 text-td-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {selectedInstallerIds.length === 0
            ? 'Installateurs'
            : `${selectedInstallerIds.length} geselecteerd`}
        </button>
        {showPicker && fixedPos && createPortal(
          <div ref={pickerRef} style={{ position: 'fixed', zIndex: 9999 }}>
            <InstallerToggleDropdown
              userMap={userMap}
              categoryMap={categoryMap}
              selectedIds={selectedInstallerIds}
              onToggle={handleToggleInstaller}
              onClose={() => setShowPicker(false)}
              fixedPos={fixedPos}
            />
          </div>,
          document.body,
        )}
        <button
          type="submit"
          disabled={scheduling || !date}
          className="bg-td-blue text-white text-xs px-3 py-1 rounded-lg hover:bg-td-blue/90 disabled:opacity-50"
        >
          {scheduling ? 'Bezig...' : 'Plannen'}
        </button>
        <button
          type="button"
          onClick={() => { setExpanded(false); setError(null); setShowPicker(false); }}
          className="text-xs text-td-muted hover:text-td-navy"
        >
          Annuleren
        </button>
      </div>
      {selectedInstallerIds.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedInstallerIds.map((id) => (
            <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-td-blue/10 text-td-blue rounded-full">
              {userMap.get(id) || `#${id}`}
              <button
                type="button"
                onClick={() => handleToggleInstaller(id)}
                className="hover:text-red-500"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </form>
  );
}

function CreateFaseForm({ projectId }: { projectId: number }) {
  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreating(true);
    setError(null);
    try {
      await createProjectFase(projectId, trimmed);
      setName('');
      setExpanded(false);
      await useProjectViewStore.getState().refreshProject(projectId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fase aanmaken mislukt');
    }
    setCreating(false);
  };

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => { setExpanded(true); setError(null); }}
        className="w-full border-2 border-dashed border-gray-300 rounded-xl p-3 text-sm text-td-muted hover:border-td-blue hover:text-td-blue transition-colors flex items-center justify-center gap-2"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Nieuwe fase
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="border border-gray-200 rounded-xl p-3 bg-white">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Fase naam..."
          autoFocus
          maxLength={200}
          className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-td-blue/40"
        />
        <button
          type="submit"
          disabled={creating || !name.trim()}
          className="bg-td-blue text-white text-sm px-4 py-1.5 rounded-lg hover:bg-td-blue/90 disabled:opacity-50"
        >
          {creating ? 'Bezig...' : 'Aanmaken'}
        </button>
        <button
          type="button"
          onClick={() => { setExpanded(false); setError(null); }}
          className="text-sm text-td-muted hover:text-td-navy"
        >
          Annuleren
        </button>
      </div>
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
    </form>
  );
}

function FaseGroup({
  projectId,
  fase,
  children,
  currentTaskId,
  installerNames,
  defaultOpen,
  canEdit,
}: {
  projectId: number;
  fase: ProjectTask;
  children: ProjectTask[];
  currentTaskId: number | null;
  installerNames: Record<string, string>;
  defaultOpen: boolean;
  canEdit: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const dateRange = getFaseDateRange(children);

  return (
    <div>
      <div className="flex items-stretch gap-0">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center justify-center w-8 shrink-0 text-td-muted hover:text-td-navy transition-colors"
          aria-label={open ? 'Inklappen' : 'Uitklappen'}
        >
          <svg
            className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <TaskRow
            projectId={projectId}
            task={fase}
            currentTaskId={currentTaskId}
            installerNames={installerNames}
            onClick={() => setOpen(!open)}
            faseDateRange={dateRange}
          />
        </div>
      </div>

      {open && (
        <>
          {children.length > 0 && (
            <div className="space-y-1.5 mt-1.5">
              {children.map((child) => (
                <TaskRow
                  key={child.id}
                  projectId={projectId}
                  task={child}
                  currentTaskId={currentTaskId}
                  installerNames={installerNames}
                  isChild
                  canEditDate={canEdit}
                />
              ))}
            </div>
          )}
          {children.length === 0 && (
            <p className="ml-10 mt-1.5 text-xs text-td-muted italic">Nog geen taken ingepland</p>
          )}
          {canEdit && <ScheduleTaskForm projectId={projectId} faseId={fase.id} />}
        </>
      )}
    </div>
  );
}

function ParentGroup({
  projectId,
  parent,
  children,
  currentTaskId,
  installerNames,
  defaultOpen,
}: {
  projectId: number;
  parent: ProjectTask;
  children: ProjectTask[];
  currentTaskId: number | null;
  installerNames: Record<string, string>;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const dateRange = getFaseDateRange(children);

  return (
    <div>
      <div className="flex items-stretch gap-0">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center justify-center w-8 shrink-0 text-td-muted hover:text-td-navy transition-colors"
          aria-label={open ? 'Inklappen' : 'Uitklappen'}
        >
          <svg
            className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <TaskRow
            projectId={projectId}
            task={parent}
            currentTaskId={currentTaskId}
            installerNames={installerNames}
            onClick={() => setOpen(!open)}
            faseDateRange={dateRange}
          />
        </div>
      </div>

      {open && children.length > 0 && (
        <div className="space-y-1.5 mt-1.5">
          {children.map((child) => (
            <TaskRow
              key={child.id}
              projectId={projectId}
              task={child}
              currentTaskId={currentTaskId}
              installerNames={installerNames}
              isChild
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function PlanningTab({ projectId, projectTasks, currentTaskId, installerNames }: PlanningTabProps) {
  const uploadError = useProjectViewStore((s) => s.uploadError);
  const dismissError = () => useProjectViewStore.setState({ uploadError: null });
  const canEditPlanning = usePermissionsStore((s) => s.canFeature('planning_edit'));

  // Include planning tasks + their parents + all Fase Indeling root tasks (even empty)
  const planningTaskIds = new Set(
    projectTasks
      .filter((t) => t.x_studio_weergeven_in_planning)
      .map((t) => t.id)
  );

  const parentIds = new Set(
    projectTasks
      .filter((t): t is ProjectTask & { parent_id: [number, string] } =>
        !!t.x_studio_weergeven_in_planning && Array.isArray(t.parent_id))
      .map((t) => t.parent_id[0])
  );

  const filteredTasks = projectTasks.filter(
    (t) => planningTaskIds.has(t.id) || parentIds.has(t.id) || isFaseTask(t)
  );

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    if (!a.date_deadline && !b.date_deadline) return 0;
    if (!a.date_deadline) return 1;
    if (!b.date_deadline) return -1;
    return a.date_deadline.localeCompare(b.date_deadline);
  });

  const { roots, childrenOf } = buildTree(sortedTasks);

  const currentTask = filteredTasks.find((t) => t.id === currentTaskId);
  const currentParentId = currentTask?.parent_id ? currentTask.parent_id[0] : null;

  const hasFases = roots.some((r) => isFaseTask(r));
  const hasContent = filteredTasks.length > 0;

  return (
    <div>
      {uploadError && (
        <div className="mb-4 bg-red-50 text-red-600 p-4 rounded-xl flex items-start gap-3" role="alert">
          <div className="flex-1">
            <p className="font-semibold text-sm">Er ging iets mis</p>
            <p className="text-sm mt-0.5">{uploadError}</p>
          </div>
          <button
            type="button"
            onClick={dismissError}
            className="shrink-0 text-red-400 hover:text-red-600 transition-colors"
            aria-label="Foutmelding sluiten"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className="mb-5">
        <h2 className="text-lg font-extrabold text-td-navy">Fase-indeling</h2>
        {hasContent && (
          <p className="text-sm text-td-muted mt-0.5">
            {filteredTasks.length} {filteredTasks.length === 1 ? 'planningstaak' : 'planningstaken'}
          </p>
        )}
      </div>

      {!hasContent && !canEditPlanning && (
        <div className="text-center py-12">
          <p className="text-td-muted text-sm">Geen planningstaken geconfigureerd</p>
        </div>
      )}

      {!hasContent && canEditPlanning && (
        <div className="text-center py-8 mb-4">
          <p className="text-td-muted text-sm mb-4">Geen fases in dit project</p>
        </div>
      )}

      {hasContent && (
        <div className="space-y-2">
          {roots.map((root) => {
            const children = childrenOf.get(root.id) || [];
            if (isFaseTask(root)) {
              const shouldOpen = root.id === currentTaskId || root.id === currentParentId || children.length === 0;
              return (
                <FaseGroup
                  key={root.id}
                  projectId={projectId}
                  fase={root}
                  children={children}
                  currentTaskId={currentTaskId}
                  installerNames={installerNames}
                  defaultOpen={shouldOpen}
                  canEdit={canEditPlanning}
                />
              );
            }
            if (children.length > 0) {
              const shouldOpen = root.id === currentTaskId || root.id === currentParentId;
              return (
                <ParentGroup
                  key={root.id}
                  projectId={projectId}
                  parent={root}
                  children={children}
                  currentTaskId={currentTaskId}
                  installerNames={installerNames}
                  defaultOpen={shouldOpen}
                />
              );
            }
            return (
              <TaskRow
                key={root.id}
                projectId={projectId}
                task={root}
                currentTaskId={currentTaskId}
                installerNames={installerNames}
              />
            );
          })}
        </div>
      )}

      {canEditPlanning && (
        <div className={hasContent ? 'mt-3' : ''}>
          <CreateFaseForm projectId={projectId} />
        </div>
      )}
    </div>
  );
}
